import { useTranslation } from 'react-i18next'
import { shallowEqual, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { format } from 'date-fns'
import routes from '@/routes'
import useTableSort from '@/hooks/table/use-table-sort.hook'

import { bonusEngineSelector } from '@/state/bonus-engine/slices/index.slices'
import {
  GlobalUIActions,
  uiGlobalSelector,
} from '@/state/ui/global/slices/index.slices'
import { getBonusType, getDateForTable, getDateTimeFromIsoUTC } from '@/utils'

import { useAppDispatch } from '@/state'
import { BonusTriggerType, TimeZoneUTCType } from '@/enums'
import { dateTimeTableFormat } from '@/constants/date'

import keys from '@/utils/keys'
import {
  BonusTrigger,
  XlsxBonusProgramType,
  BonusLibraryColumnProps,
} from '@/state/bonus-engine/models'

import { TableHeadCellPropsType } from '@/state/ui/global/models'
import { useBonusActions } from './use-bonuses-actions.hook'

export const useBonuses = () => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()

  const navigate = useNavigate()

  const { bonuses, pagination, isLoading } = useSelector(
    bonusEngineSelector,
    shallowEqual,
  )

  const { isLoadingFull } = useSelector(uiGlobalSelector)

  const { deleteBonus } = useBonusActions()

  const getTriggerBy = (trigger?: BonusTrigger) => {
    return [
      ...keys(BonusTriggerType)
        .filter(
          (item) =>
            trigger?.triggerBy !== undefined &&
            BonusTriggerType[item] === trigger?.triggerBy,
        )
        .map((x) => {
          return {
            value: t(`bonusTriggerTypes.${x}`),
          }
        }),
    ]
  }

  const headCells: TableHeadCellPropsType<BonusLibraryColumnProps>[] = [
    { id: 'isActive', label: t('labels.status'), width: 150, sort: true },
    {
      id: 'createdAtNumber',
      label: t('labels.createdDate'),
      width: 180,
      sort: true,
    },
    { id: 'name', label: t('labels.name'), width: 250, sort: true },
    { id: 'code', label: t('labels.code'), width: 250, sort: true },
    {
      id: 'triggerText',
      label: t('labels.trigger'),
      width: 250,
      sort: true,
    },
    { id: 'type', label: t('labels.type'), sort: true },
  ]

  const sortedInitialState = 'createdAtNumber'

  const formattedBonus =
    bonuses &&
    bonuses.map((bonus) => {
      return {
        ...bonus,
        createdAt: bonus.createdAt
          ? getDateForTable(
              `${format(
                getDateTimeFromIsoUTC(
                  bonus.createdAt,
                  TimeZoneUTCType['GMT-07 Pacific Time'],
                ),
                dateTimeTableFormat,
              )} PT`,
            )
          : '-',
        createdAtNumber: bonus.createdAt
          ? new Date(bonus.createdAt).getTime()
          : '-',
        type: bonus.type ? getBonusType(bonus.type) : '-',
        triggerText: bonus.trigger
          ? getTriggerBy(bonus.trigger)[0]?.value
          : '-',
      }
    })

  const getXlsFormattedRow = (
    row: BonusLibraryColumnProps,
  ): XlsxBonusProgramType => {
    return {
      name: row.name,
      code: row.code,
      isActive: `${row.isActive ? t('labels.active') : t('labels.inactive')}`,
      createdAtNumber: row.createdAt
        ? `${format(
            getDateTimeFromIsoUTC(
              row.createdAt.toString(),
              TimeZoneUTCType['GMT-07 Pacific Time'],
            ),
            dateTimeTableFormat,
          )} PT`
        : '-',
      triggerText: getTriggerBy(row.trigger)[0].value,
      type: row.type ? getBonusType(row.type) : '-',
    }
  }

  const { sortedRows, createSortHandler, order, orderBy } = useTableSort<
    BonusLibraryColumnProps,
    keyof BonusLibraryColumnProps
  >(formattedBonus, sortedInitialState, 'desc')

  const handleCreateBonus = () => {
    navigate(routes.bonus.CREATE_BONUS)
  }

  const handleDeleteBonus = (
    anchorPosition: { top: number; left: number },
    bonusId: string,
  ) => {
    dispatch(
      GlobalUIActions.setPopoverConfirmAction({
        action: t('messages.delete'),
        anchorPosition,
        open: true,
        type: t('bonus').toLowerCase(),
        handler: () => deleteBonus({ _id: bonusId }),
      }),
    )
  }

  const handleEditBonus = (bonusId: string) => {
    navigate(`${routes.bonus.UPDATE_BONUS}/${bonusId}`)
  }

  const handleDuplicateBonus = (bonusId: string) => {
    navigate(`${routes.bonus.DUPLICATE_BONUS}/${bonusId}`)
  }

  return {
    sortedRows,
    createSortHandler,
    order,
    orderBy,
    pagination,
    isLoading,
    isLoadingFull,
    headCells,
    formattedBonus,
    getXlsFormattedRow,
    handleDeleteBonus,
    handleCreateBonus,
    handleEditBonus,
    handleDuplicateBonus,
  }
}
